var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Button from '@web/components/layout/Button';
import Loading from '@web/components/layout/Loading';
import Table from '@web/components/layout/Table/Table';
import { ActivityLabelModal } from '@web/components/Settings/ActivityLabels/ActivityLabelModal';
import DeleteActivityLabelModal from '@web/components/Settings/ActivityLabels/DeleteActivityLabelModal';
import GeneratedLabelsModal from '@web/components/Settings/ActivityLabels/GeneratedLabelsModal';
import GenerateLabelsButton from '@web/components/Settings/ActivityLabels/GenerateLabelsButton';
import { IS_DEMO } from '@web/config';
import { useToast } from '@web/hocs/useToast';
import { trpc } from '@web/lib/trpc';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
export var ActivityLabels = function () {
    var t = useTranslation().t;
    var toast = useToast().toast;
    var _a = trpc.getAllLabels.useQuery(), labels = _a.data, labelsRefetch = _a.refetch, labelsLoading = _a.isLoading;
    var createOrUpdateLabels = trpc.createOrUpdateLabels.useMutation({
        onSuccess: function () {
            labelsRefetch();
            toast({
                title: t('app.settings.activityLabels.labelSaved'),
                variant: 'success',
                duration: 3000
            });
        }
    });
    var deleteLabel = trpc.deleteLabel.useMutation({
        onSuccess: function () {
            labelsRefetch();
            toast({
                title: t('app.settings.activityLabels.labelDeleted'),
                variant: 'success',
                duration: 3000
            });
        }
    });
    var _b = useState(null), selectedLabel = _b[0], setSelectedLabel = _b[1];
    var _c = useState(null), labelToDelete = _c[0], setLabelToDelete = _c[1];
    var _d = useState([]), generatedLabels = _d[0], setGeneratedLabels = _d[1];
    var _e = useState(false), isModalVisible = _e[0], setIsModalVisible = _e[1];
    var _f = useState(false), isGenerating = _f[0], setIsGenerating = _f[1];
    var _g = useState(false), isGeneratedLabelsModalVisible = _g[0], setIsGeneratedLabelsModalVisible = _g[1];
    if (labelsLoading) {
        return _jsx(Loading, {});
    }
    var handleEdit = function (label) {
        setSelectedLabel(label);
        setIsModalVisible(true);
    };
    var handleDeleteClick = function (label) {
        setLabelToDelete(label);
    };
    var handleConfirmDelete = function () {
        if (IS_DEMO || !(labelToDelete === null || labelToDelete === void 0 ? void 0 : labelToDelete.id)) {
            setLabelToDelete(null);
            return;
        }
        deleteLabel.mutate({ id: labelToDelete.id });
        setLabelToDelete(null);
    };
    var handleSave = function (label) {
        if (IS_DEMO) {
            setIsModalVisible(false);
            setSelectedLabel(null);
            return;
        }
        createOrUpdateLabels.mutate({ labels: [label] });
        setIsModalVisible(false);
        setSelectedLabel(null);
    };
    var columns = [
        {
            title: t('app.settings.activityLabels.labelName'),
            isVisible: true,
            className: 'w-2/6 sm:px-3',
            colspan: 1
        },
        {
            title: t('app.settings.activityLabels.rules'),
            isVisible: true,
            className: 'w-3/6 sm:px-3',
            colspan: 2
        },
        {
            title: '',
            isVisible: true,
            className: 'w-1/6 sm:px-3 sm:text-end',
            colspan: 3
        }
    ];
    var tableData = (labels === null || labels === void 0 ? void 0 : labels.map(function (label) { return [
        {
            id: "".concat(label.id, "-name"),
            render: function () { return _jsx("span", { children: label.name }); },
            colspan: 1
        },
        {
            id: "".concat(label.id, "-rules"),
            render: function () { return (_jsx("p", __assign({ className: "text-xs text-gray-500 font-normal" }, { children: label.rules
                    .map(function (rule) {
                    return rule.conditions
                        .map(function (condition) {
                        return "".concat(t("app.settings.activityLabels.targets.".concat(rule.target)), " ").concat(t("app.settings.activityLabels.conditionTypes.".concat(condition.type)), " \"").concat(condition.keyword, "\"");
                    })
                        .join(' AND ');
                })
                    .join(', ') }))); },
            colspan: 2
        },
        {
            id: "".concat(label.id, "-actions"),
            render: function () { return (_jsxs("div", __assign({ className: "flex justify-end gap-2" }, { children: [_jsx("div", __assign({ className: "flex" }, { children: _jsx(PencilIcon, { className: "h-5 w-5 text-indigo-600 cursor-pointer hover:text-indigo-800 hover:scale-105", onClick: function () { return handleEdit(label); } }) })), _jsx("div", __assign({ className: "flex" }, { children: _jsx(TrashIcon, { className: "h-5 w-5 text-indigo-600 cursor-pointer hover:text-indigo-800 hover:scale-105", onClick: function () {
                                return handleDeleteClick(label);
                            } }) }))] }))); },
            colspan: 3
        }
    ]; })) || [];
    return (_jsxs("div", __assign({ className: "space-y-4" }, { children: [_jsxs("div", __assign({ className: "flex justify-end gap-2" }, { children: [_jsx(GenerateLabelsButton, { isGenerating: isGenerating, setIsGenerating: setIsGenerating, setGeneratedLabels: setGeneratedLabels, setIsGeneratedLabelsModalVisible: setIsGeneratedLabelsModalVisible }), _jsxs(Button, __assign({ variant: "primary", onClick: function () {
                            setSelectedLabel(null);
                            setIsModalVisible(true);
                        }, className: "flex items-center gap-2" }, { children: [_jsx(PlusIcon, { className: "h-5 w-5" }), t('app.settings.activityLabels.create')] }))] })), _jsx(Table, { columns: columns, data: tableData, striped: true }), _jsx(ActivityLabelModal, { visible: isModalVisible, onClose: function () {
                    setIsModalVisible(false);
                    setSelectedLabel(null);
                }, label: selectedLabel, onSave: handleSave }), _jsx(DeleteActivityLabelModal, { visible: !!labelToDelete, onClose: function () { return setLabelToDelete(null); }, onConfirm: handleConfirmDelete }), _jsx(GeneratedLabelsModal, { visible: isGeneratedLabelsModalVisible, labels: generatedLabels, onClose: function () { return setIsGeneratedLabelsModalVisible(false); }, onSave: function (labels) {
                    if (IS_DEMO) {
                        setIsGeneratedLabelsModalVisible(false);
                        setGeneratedLabels([]);
                        return;
                    }
                    createOrUpdateLabels.mutate({ labels: labels });
                    labelsRefetch();
                    setIsGeneratedLabelsModalVisible(false);
                    setGeneratedLabels([]);
                } })] })));
};
